import React, { useState, useEffect, useMemo } from 'react';
import { FaArrowLeft, FaArrowRight, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// ============ CONFIG ============
const DAYS_VISIBLE = 9; // number of days shown in the calendar
const PADDING_DAYS = 30; // how many extra days to fetch on either side
const FETCH_TIMEOUT_MS = 20000;

// ============ HELPERS ============

// Get an array of Date objects, starting from startDate, for `days` days.
const getDatesInRange = (startDate, days) => {
  const dates = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  for (let i = 0; i < days; i++) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

// Local-time YYYY-MM-DD key. Never uses toISOString (which shifts by TZ).
const toLocalDateKey = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Inclusive range check on local date keys.
const isDateInRange = (date, checkInDate, checkOutDate) => {
  const d = toLocalDateKey(date);
  const ci = toLocalDateKey(checkInDate);
  const co = toLocalDateKey(checkOutDate);
  if (!d || !ci || !co) return false;
  return d >= ci && d <= co;
};

// Defensive getters — the API could name these fields differently.
// We check multiple common variants and return the first non-empty one.
const getGuestPhone = (booking) =>
  booking?.phonenumber ||
  booking?.phone_number ||
  booking?.phone ||
  booking?.guestphone ||
  booking?.mobile ||
  booking?.guest_phone ||
  booking?.mobile_number ||
  '';

const getGuestEmail = (booking) =>
  booking?.emailid ||
  booking?.email_id ||
  booking?.email ||
  booking?.guestemail ||
  booking?.guest_email ||
  '';

// Contact line = phone if present, else email
const getGuestContact = (booking) => {
  const phone = String(getGuestPhone(booking) || '').trim();
  if (phone) return { type: 'phone', value: phone };
  const email = String(getGuestEmail(booking) || '').trim();
  if (email) return { type: 'email', value: email };
  return { type: 'none', value: '' };
};

const RoomAvailabilityCalendar = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Start of current week (Sunday). Swap to `new Date(today)` if you want
  // the window to start on today instead.
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const [currentStartDate, setCurrentStartDate] = useState(startOfWeek);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // ============ FETCH ============
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const fetchStart = new Date(currentStartDate);
    fetchStart.setHours(0, 0, 0, 0);
    fetchStart.setDate(fetchStart.getDate() - PADDING_DAYS);

    const fetchEnd = new Date(currentStartDate);
    fetchEnd.setHours(0, 0, 0, 0);
    fetchEnd.setDate(fetchEnd.getDate() + (DAYS_VISIBLE - 1) + PADDING_DAYS);

    const startDate = toLocalDateKey(fetchStart);
    const endDateStr = toLocalDateKey(fetchEnd);

    const url = `${BQ_BASE_URL}/bq/api/search_bookings_with_guests_and_roomtype?start_date=${encodeURIComponent(
      startDate
    )}&end_date=${encodeURIComponent(endDateStr)}`;

    console.log('Fetching bookings:', url);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { accept: 'application/json' },
      });

      const rawText = await response.text();

      if (!response.ok) {
        console.error('Non-OK response:', response.status, rawText);
        throw new Error(`Server ${response.status}${rawText ? ': ' + rawText.slice(0, 140) : ''}`);
      }

      let data;
      try {
        data = rawText ? JSON.parse(rawText) : [];
      } catch (parseErr) {
        console.error('Invalid JSON:', rawText.slice(0, 200));
        throw new Error('Server returned invalid JSON');
      }

      // Debug: log first booking so you can confirm the phone/email field names
      if (Array.isArray(data) && data.length > 0) {
        console.log('Sample booking object:', data[0]);
      }

      if (Array.isArray(data)) {
        setBookings(data);
      } else if (data && Array.isArray(data.bookings)) {
        setBookings(data.bookings);
      } else {
        setBookings([]);
      }
    } catch (err) {
      let friendly = err.message || 'Unknown error';
      if (err.name === 'AbortError') {
        friendly = `Request timed out after ${FETCH_TIMEOUT_MS / 1000}s. Try refreshing or contact support.`;
      } else if (/Failed to fetch/i.test(friendly)) {
        friendly = 'Cannot reach server. Check your internet, backend status, or CORS settings.';
      }
      setError(friendly);
      console.error('Fetch error:', err);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStartDate]);

  // ============ DATE RANGE FOR DISPLAY ============
  const datesInRange = useMemo(
    () => getDatesInRange(currentStartDate, DAYS_VISIBLE),
    [currentStartDate]
  );

  const todayKey = toLocalDateKey(today);

  // ============ NAVIGATION ============
  const handlePeriodChange = (direction) => {
    const newStartDate = new Date(currentStartDate);
    newStartDate.setDate(newStartDate.getDate() + direction * DAYS_VISIBLE);
    setCurrentStartDate(newStartDate);
  };

  const goToToday = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const start = new Date(t);
    start.setDate(t.getDate() - t.getDay());
    setCurrentStartDate(start);
  };

  // ============ FILTERING ============
  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const matchesRoomType = selectedRoomType === '' || booking.roomtypename === selectedRoomType;

      const q = searchQuery.trim().toLowerCase();
      const phone = String(getGuestPhone(booking) || '').toLowerCase();
      const email = String(getGuestEmail(booking) || '').toLowerCase();

      const matchesSearchQuery =
        q === '' ||
        String(booking.roomnumber || '').includes(q) ||
        `${booking.firstname || ''} ${booking.lastname || ''}`.toLowerCase().includes(q) ||
        phone.includes(q) ||
        email.includes(q);

      return matchesRoomType && matchesSearchQuery;
    });
  }, [bookings, selectedRoomType, searchQuery]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedRoomType('');
  };

  // ============ RENDER ============
  return (
    <div className="flex flex-col p-6 bg-gray-100 min-h-screen">
      {/* NAV HEADER */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
        <div className="flex space-x-6">
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
            }
            className="btn"
          >
            Front Desk Operations
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/guest-managment')
            }
            className="btn text-black font-bold"
          >
            Guest Management
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
            }
            className="btn"
          >
            Hospitality Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
            }
            className="btn"
          >
            Facility Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/billing-payments')
            }
            className="btn"
          >
            Billing & Payments
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
            }
            className="btn"
          >
            Refunds & Adjustments
          </button>
        </div>
      </header>

      {/* TITLE */}
      <div className="p-4 text-center">
        <h1 className="text-2xl font-bold text-black">Room Reservation Calendar</h1>
        <p className="text-sm text-black">
          View, manage, and track room reservations for the next {DAYS_VISIBLE} days.
        </p>
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap w-full gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <input
            type="text"
            placeholder="Search by Room no, Guest name, Phone, or Email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 pl-10 border rounded"
          />
          <FaSearch
            size={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
          />
        </div>

        <select
          value={selectedRoomType}
          onChange={(e) => setSelectedRoomType(e.target.value)}
          className="p-2 border rounded outline-none"
        >
          <option value="">All Room Types</option>
          {[...new Set(bookings.map((b) => b.roomtypename).filter(Boolean))].map((roomType) => (
            <option key={roomType} value={roomType} className="capitalize">
              {roomType}
            </option>
          ))}
        </select>

        <button
          onClick={clearFilters}
          className="px-4 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Clear Filters
        </button>

        <button onClick={goToToday} className="px-4 bg-black text-white rounded hover:bg-gray-800">
          Today
        </button>

        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* PERIOD NAV */}
      <div className="flex justify-between w-full items-center p-3 bg-black">
        <button
          onClick={() => handlePeriodChange(-1)}
          className="px-4 py-2 bg-white text-black rounded hover:bg-gray-100"
          title="Previous"
        >
          <FaArrowLeft size={18} />
        </button>

        <div className="text-base sm:text-lg font-bold text-white text-center px-4">
          {datesInRange[0].toLocaleDateString(undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}{' '}
          →{' '}
          {datesInRange[DAYS_VISIBLE - 1].toLocaleDateString(undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </div>

        <button
          onClick={() => handlePeriodChange(1)}
          className="px-4 py-2 bg-white text-black rounded hover:bg-gray-100"
          title="Next"
        >
          <FaArrowRight size={18} />
        </button>
      </div>

      {/* CONTENT */}
      <div className="flex-1 bg-gray-100 mt-3">
        {loading ? (
          <div className="text-center w-full py-16 text-gray-700 font-medium">
            Loading reservations…
          </div>
        ) : error ? (
          <div className="text-center w-full py-16">
            <div className="text-red-600 font-semibold mb-2">Error loading calendar</div>
            <div className="text-sm text-red-500 max-w-xl mx-auto">{error}</div>
            <button
              onClick={fetchData}
              className="mt-4 px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            >
              Try Again
            </button>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="flex items-center justify-center h-96 text-black font-bold text-2xl">
            No reservations for this period.
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-300 rounded bg-white">
            <table className="table-auto w-full border-collapse">
              <thead>
                <tr>
                  <th className="border border-gray-300 p-2 bg-gray-200 text-black text-left w-28 sticky left-0 z-20">
                    Room Type
                  </th>
                  <th className="border border-gray-300 p-2 bg-gray-200 text-black w-20 sticky left-28 z-20">
                    Room No.
                  </th>
                  {datesInRange.map((date, idx) => {
                    const isToday = toLocalDateKey(date) === todayKey;
                    return (
                      <th
                        key={idx}
                        className={`border border-gray-300 p-2 text-black whitespace-nowrap ${
                          isToday ? 'bg-blue-200' : 'bg-gray-200'
                        }`}
                        style={{ minWidth: '110px' }}
                      >
                        <div className="text-xs font-bold">
                          {date.toLocaleDateString(undefined, { weekday: 'short' })}
                        </div>
                        <div className="text-sm">
                          {date.toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {filteredBookings.map((booking, bookingIdx) => {
                  const contact = getGuestContact(booking);
                  const guestName = `${booking.firstname || ''} ${booking.lastname || ''}`.trim();

                  return (
                    <tr key={bookingIdx} className="border">
                      <td className="border border-gray-300 p-3 text-center font-bold capitalize sticky left-0 bg-white z-10">
                        {booking.roomtypename}
                      </td>
                      <td className="border border-gray-300 p-2 text-center sticky left-28 bg-white z-10">
                        {booking.roomnumber}
                      </td>

                      {datesInRange.map((date, dateIdx) => {
                        const cellKey = toLocalDateKey(date);
                        const checkInKey = toLocalDateKey(booking.checkindate);
                        const checkOutKey = toLocalDateKey(booking.checkoutdate);

                        const inRange = isDateInRange(
                          date,
                          booking.checkindate,
                          booking.checkoutdate
                        );

                        const isCheckInDay = cellKey === checkInKey;
                        const isToday = cellKey === todayKey;

                        let cellClass = 'border border-gray-300 p-1 text-center relative';
                        if (inRange) cellClass += ' bg-black text-white';
                        else if (isToday) cellClass += ' bg-blue-50';

                        return (
                          <td
                            key={`${booking.roomnumber || booking.bookingid}-${dateIdx}`}
                            className={cellClass}
                            style={{ width: '110px', minWidth: '110px', height: '58px' }}
                          >
                            {/* In-range bar visual hint */}
                            {inRange && (
                              <div
                                className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 "
                                aria-hidden
                              />
                            )}

                            {isCheckInDay && guestName && (
                              <div
                                data-tooltip-id={`tooltip-${booking.bookingid || bookingIdx}`}
                                data-tooltip-content={`${guestName}${
                                  contact.value
                                    ? contact.type === 'phone'
                                      ? ` • 📞 ${contact.value}`
                                      : ` • ✉️ ${contact.value}`
                                    : ''
                                } • In: ${checkInKey}${checkOutKey ? ` • Out: ${checkOutKey}` : ''}`}
                                className="relative z-10 flex flex-col items-center justify-center leading-tight px-1"
                              >
                                {/* NAME */}
                                <span
                                  className="text-[11px] font-bold truncate w-full text-center"
                                  title={guestName}
                                >
                                  {guestName.length > 16 ? guestName.slice(0, 15) + '…' : guestName}
                                </span>

                                {/* PHONE (fallback to EMAIL) */}
                                {contact.value && (
                                  <span
                                    className={`text-[10px] font-medium truncate w-full text-center ${
                                      contact.type === 'phone'
                                        ? 'text-emerald-300'
                                        : 'text-blue-300'
                                    }`}
                                    title={contact.value}
                                  >
                                    {contact.type === 'phone' ? '📞 ' : '✉️ '}
                                    {contact.value.length > 16
                                      ? contact.value.slice(0, 15) + '…'
                                      : contact.value}
                                  </span>
                                )}
                              </div>
                            )}

                            <Tooltip id={`tooltip-${booking.bookingid || bookingIdx}`} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomAvailabilityCalendar;
